<script lang="ts">
  // libs
  import { groupBy } from "lodash";
  import { pluralize } from "@shared/svelte/Modules/pluralize";

  // components
  import IntegratedTooltipIcon from "@shared/svelte/Components/IntegratedTooltipIcon.svelte";
  import FormatMoney from "@shared/svelte/Components/FormatMoney.svelte";
  import QuantityDealText from "@shared/svelte/Components/Deal/QuantityDealText.svelte";
  import { stagedRolloutCheck } from "@shared/svelte/Modules/StagedRolloutCheck.svelte";
  import { shouldHideAllDistributorInfo } from "@shared/services/retailer_user_authentication_service";
  import RetailerUserAuthenticationsStore from "@retailer/svelte/stores/RetailerUserAuthenticationsStore";

  // types
  import type { CartProduct } from "@shared/types/ProductLine";
  import type { DistributorCart } from "@shared/types/Distributor";
  import { DISCOUNT_AMOUNT_TYPES } from "@shared/types/Deals";

  import {
    OHLQ_DISTRIBUTOR_ID,
    VT_802_SPIRITS_DISTRIBUTOR_ID,
  } from "@shared/services/retailer_user_authentication/constants";

  // props
  export let total: number;
  export let subtotal: number;
  export let taxTotal: number;
  export let orderProducts: CartProduct[];
  export let products: CartProduct[];
  export let distributor: DistributorCart;
  export let totalItems: number;

  if (!products && orderProducts) {
    products = orderProducts;
  }

  // data
  let appliedMMProductDiscounts: any = [];
  let appliedQtyProductDiscounts: any = [];
  let totalSavings: number = 0;

  // functions
  const gatherAppliedDealGroups = () => {
    return groupBy(
      products.filter(op =>
        op.discounts?.some(
          d =>
            d.discount?.deal_tier_id &&
            d.discount?.amount_type !== DISCOUNT_AMOUNT_TYPES.RIP &&
            !d.just_an_upsell,
        ),
      ),
      p => p.discounts.find(d => d.discount?.deal_tier_id)?.discount.deal_tier_id,
    );
  };
  const gatherAppliedMMProductDiscounts = () => {
    const appliedDealGroups = gatherAppliedDealGroups();

    if (!appliedDealGroups) return [];

    return Object.entries(appliedDealGroups)
      .map(([dealTierId, products]: any) => {
        let mmDiscount;

        for (let orderProduct of products) {
          const matchingOrderDiscount = orderProduct.discounts.find(
            orderProductDiscount =>
              orderProductDiscount.discount?.deal_tier_id === Number(dealTierId) &&
              !orderProductDiscount.just_an_upsell,
          );

          if (matchingOrderDiscount) {
            mmDiscount = matchingOrderDiscount.discount;
            break;
          }
        }

        return {
          discount: mmDiscount,
          total: products.reduce((sum: number, product: CartProduct) => {
            return sum + product.discount_amount;
          }, 0),
        };
      })
      .filter(n => n.discount && n.total > 0);
  };
  const gatherAppliedQtyProductDiscounts = () => {
    return products
      .map((product: CartProduct) => {
        const qtyDiscount = product.discounts?.find(
          d => d.discount?.id && !d.discount?.deal_tier_id && !d.just_an_upsell,
        );

        return {
          product: product,
          discount: qtyDiscount,
        };
      })
      .filter(n => n.discount && n.product.discount_amount > 0);
  };
  const runDiscountCalculations = () => {
    return (
      appliedMMProductDiscounts.reduce((sum, discount) => sum + discount.total, 0) +
      appliedQtyProductDiscounts.reduce(
        (sum, productDiscount) => sum + productDiscount.product.discount_amount,
        0,
      )
    );
  };

  let subtotalAvailable;
  $: {
    subtotalAvailable = total > 0 || (total === 0 && totalSavings === 0 && subtotal > 0);
  }

  // reactives
  $: {
    total = total;
    subtotal = subtotal;
    taxTotal = taxTotal;
    products = products;
    orderProducts = orderProducts;
  }
  $: if (total !== subtotal) {
    appliedMMProductDiscounts = gatherAppliedMMProductDiscounts();
    appliedQtyProductDiscounts = gatherAppliedQtyProductDiscounts();
    totalSavings = runDiscountCalculations();
  }
  $: hasDeals =
    total !== subtotal &&
    (appliedMMProductDiscounts.length > 0 || appliedQtyProductDiscounts.length > 0);
  $: itemCountText = pluralize("items", totalItems, true);
  $: dealCountText = pluralize(
    "deals",
    appliedMMProductDiscounts.length + appliedQtyProductDiscounts.length,
    true,
  );

  const hasRetailerUserAuthRollout = stagedRolloutCheck(
    "marketplace__retailer_user_authentication",
  );

  let hideAllDistributorInfo: boolean = false;
  $: {
    hideAllDistributorInfo =
      hasRetailerUserAuthRollout &&
      distributor?.id &&
      shouldHideAllDistributorInfo($RetailerUserAuthenticationsStore, [distributor.id]);
  }

  const isIntegrated =
    distributor.integrated || (distributor.seven_fifty_integration && distributor.uses_price_zones);

  const taxLabel = () => {
    if (distributor.id === OHLQ_DISTRIBUTOR_ID) {
      return "Cuyahoga County Gallonage Tax";
    } else if (distributor.id === VT_802_SPIRITS_DISTRIBUTOR_ID) {
      return "Bottle Deposit Total";
    } else {
      return "Tax";
    }
  };
</script>

<div
  class="fizz-full-width fizz-surface-gray fizz-radius-medium fizz-inset-horizontal-16 fizz-inset-vertical-12"
>
  <div
    data-testid="order-summary-subtotal"
    class="fizz-flex-row fizz-full-width fizz-space-between fizz-flow-4 fizz-stack-4"
  >
    <p>{"Items subtotal (" + itemCountText + ")"}</p>
    {#if subtotalAvailable}
      <FormatMoney input={subtotal} />
    {:else}
      <p class="fizz-text-subdued fizz-text-right">Subtotal not available</p>
    {/if}
  </div>

  {#if hasDeals}
    <div class="fizz-flex-row fizz-full-width fizz-space-between fizz-flow-4 fizz-stack-4">
      <div class="fizz-tooltip-container">
        <div
          class="fizz-tooltip-trigger"
          id="deals-tooltip"
          aria-describedby="tooltip-desc-deals"
          style="font-weight: 400;"
        >
          <p class="fizz-text-subdued fizz-underline-dashed fizz-stack-4">
            {"Deals applied (" + dealCountText + ")"}
          </p>
        </div>
        <div class="fizz-tooltip fizz-tooltip-top" role="tooltip" id="tooltip-desc-deals">
          {#if appliedMMProductDiscounts.length > 0}
            {#each appliedMMProductDiscounts as productDiscount, dindex}
              <span data-id={dindex}>
                ✓ {productDiscount.discount.deal_tier_name} &ndash; {productDiscount.discount
                  .case_count}
                {productDiscount.discount.deal_tier_label} mix deal (-<FormatMoney
                  input={productDiscount.discount.amount}
                />/{productDiscount.discount.deal_tier_label})
              </span>
              <br />
            {/each}
          {/if}
          {#if appliedQtyProductDiscounts.length > 0}
            {#each appliedQtyProductDiscounts as productDiscount, dindex}
              <span data-id={dindex}>
                ✓ {productDiscount.product.product_line.name}
                &ndash; <QuantityDealText
                  cartProduct={productDiscount.product}
                  discount={productDiscount.discount.discount}
                  discount_id={productDiscount.discount.discount.id}
                  disableQTYText={true}
                  useLegacyLayout={false}
                />
              </span>
              <br />
            {/each}
          {/if}
        </div>
      </div>
      <div data-testid="total-deal-savings">
        <FormatMoney input={-1 * totalSavings} />
      </div>
    </div>
  {/if}

  {#if taxTotal && taxTotal > 0 && !hideAllDistributorInfo}
    <div
      data-testid="order-summary-tax-total"
      class="fizz-flex-row fizz-full-width fizz-space-between fizz-flow-4 fizz-stack-4"
    >
      <p>{taxLabel()}</p>
      <FormatMoney input={taxTotal} />
    </div>
  {/if}

  <div
    data-testid="order-summary-total"
    class="fizz-flex-row fizz-full-width fizz-space-between fizz-border-top fizz-inset-vertical-8"
  >
    <p class="fizz-heading-4">Est. order total</p>
    {#if total > 0}
      <p class="fizz-heading-4 fizz-text-right">
        <FormatMoney input={total} />
      </p>
    {:else if total === 0 && totalSavings === 0 && subtotal > 0}
      <p class="fizz-heading-4 fizz-text-right">
        <FormatMoney input={subtotal} />
      </p>
    {:else}
      <p class="fizz-heading-4 fizz-text-right">Total not available</p>
    {/if}
  </div>

  <div class="fizz-flex-row fizz-full-width fizz-space-between fizz-align-content-start">
    {#if isIntegrated}
      <IntegratedTooltipIcon {distributor} additionalClasses="fizz-inline" />
      <p class="fizz-text-subdued fizz-inset-horizontal-8 fizz-text-small">
        {distributor.name +
          " is a partner distributor that passes their prices and inventory to Provi. Any final pricing, deals, keg deposits, special discounts, and fees will be applied by your rep and appear on distributor invoices."}
      </p>
    {:else}
      <p class="fizz-text-subdued fizz-text-small">
        {distributor.name} does not provide us with pricing information. If you know prices of your items
        you can add your own
        <a class="fizz-text-underline fizz-text-subdued" href="/settings/pricing/">custom pricing</a
        > to your account. Your final invoice from your sales rep will reflect your accurate pricing.
      </p>
    {/if}
  </div>
</div>
